/**
 * MobileCustomersView — the redesigned mobile Customers screen (Trioty
 * Customers Mobile). Sibling of MobileProductsView.
 *
 * Self-contained: owns the offset-less "load more" paging (grows `limit`,
 * offset 0 so grouping/sort see the whole loaded set), the persisted Display
 * prefs (group / sort / row-detail fields) and the Filter state, and fetches
 * via useSellerCustomersQuery. Tapping a row opens the full-screen customer
 * detail via `onOpenCustomer`.
 *
 * Search deliberately does NOT live here: the portal has exactly one search
 * entry point (bottom-nav Search on mobile / global search on desktop), which
 * covers customers too.
 *
 * Grouping (Governorate / None), sorting (Most spent / Most orders / Name /
 * Recently added) and filtering (Governorate multi-select · Has orders) are all
 * backed by REAL SellerCustomerView fields — nothing is invented. The design's
 * "add customer" icon is replaced by the notification bell because there is no
 * create-customer endpoint.
 */

import { useCallback, useMemo, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { formatCurrency } from "@/lib/formatting/currency";
import type { Locale } from "@/lib/i18n/config";
import { useSellerCustomersQuery } from "@/modules/customers/services/useSellerCustomersQuery";
import { initialsOf, tintFor } from "@/modules/customers/lib/customer-avatar";
import { MobileHeaderNotificationBell } from "@/modules/notifications/components/MobileHeaderNotificationBell";
import {
  EMPTY_CUSTOMERS_FILTERS,
  NO_GOVERNORATE,
  activeFilterCount,
  governorateKey,
  governorateName,
  sortComparator,
  type CustomerItem,
  type CustomersFilterState,
  type CustomersGroupBy,
} from "./customers-mobile-model";
import {
  useCustomersDisplayPrefs,
  type RowProps,
} from "./useCustomersDisplayPrefs";
import { CustomersDisplaySheet } from "./CustomersDisplaySheet";
import {
  CustomersFilterSheet,
  type GovernorateOption,
} from "./CustomersFilterSheet";

type Props = {
  storeSlug: string;
  locale: Locale;
  /** Tapping a row opens the full-screen customer detail (owned by the page). */
  onOpenCustomer: (id: string) => void;
};

const PAGE_SIZE = 40;

type BuiltGroup = {
  key: string;
  label: string;
  showHeader: boolean;
  rows: CustomerItem[];
};

/** Groups the pool by governorate (alphabetical, No-governorate last) or
 *  returns a single ungrouped list. */
function buildGroups(
  pool: CustomerItem[],
  group: CustomersGroupBy,
  locale: Locale,
  noGovernorateLabel: string,
): BuiltGroup[] {
  if (group === "none") {
    return pool.length ? [{ key: "none", label: "", showHeader: false, rows: pool }] : [];
  }
  const buckets = new Map<string, { label: string; rows: CustomerItem[] }>();
  for (const c of pool) {
    const key = governorateKey(c);
    const label = key === NO_GOVERNORATE ? noGovernorateLabel : (governorateName(c, locale) ?? "");
    const bucket = buckets.get(key);
    if (bucket) bucket.rows.push(c);
    else buckets.set(key, { label, rows: [c] });
  }
  const named = [...buckets.entries()]
    .filter(([key]) => key !== NO_GOVERNORATE)
    .sort((a, b) => a[1].label.localeCompare(b[1].label, locale === "ar" ? "ar" : "en"));
  const out: BuiltGroup[] = named.map(([key, v]) => ({
    key,
    label: v.label,
    showHeader: true,
    rows: v.rows,
  }));
  const none = buckets.get(NO_GOVERNORATE);
  if (none) {
    out.push({ key: NO_GOVERNORATE, label: none.label, showHeader: true, rows: none.rows });
  }
  return out;
}

export function MobileCustomersView({ storeSlug, locale, onOpenCustomer }: Props) {
  const t = useTranslations();
  const uiLocale = useLocale();
  const { prefs, setGroup, setSort, toggleProp, resetProps } =
    useCustomersDisplayPrefs(storeSlug);

  const [limit, setLimit] = useState(PAGE_SIZE);
  const [filters, setFilters] = useState<CustomersFilterState>(EMPTY_CUSTOMERS_FILTERS);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [displayOpen, setDisplayOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const changeFilters = useCallback((next: CustomersFilterState) => {
    setFilters(next);
    setLimit(PAGE_SIZE);
  }, []);

  const { data, isLoading, error } = useSellerCustomersQuery({
    storeSlug,
    limit,
    offset: 0,
  });

  const allCustomers = useMemo(() => data?.items ?? [], [data?.items]);
  const total = data?.total ?? 0;
  // Backend has more rows than we've loaded → offer "Load more".
  const canLoadMore = allCustomers.length < total;

  // Governorate options for the filter sheet — distinct governorates present in
  // the loaded page (+ a "No governorate" bucket when some customers have none).
  const governorateOptions = useMemo<GovernorateOption[]>(() => {
    const seen = new Map<string, string>();
    let hasNone = false;
    for (const c of allCustomers) {
      const key = governorateKey(c);
      if (key === NO_GOVERNORATE) {
        hasNone = true;
        continue;
      }
      if (!seen.has(key)) seen.set(key, governorateName(c, uiLocale) ?? key);
    }
    const opts = [...seen.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label, uiLocale === "ar" ? "ar" : "en"));
    if (hasNone) opts.push({ id: NO_GOVERNORATE, label: t(keys.customers.mNoGovernorate) });
    return opts;
  }, [allCustomers, uiLocale, t]);

  // Filter (client-side over the loaded page) → sort.
  const filtered = useMemo(() => {
    const pool = allCustomers.filter((c) => {
      if (filters.hasOrders && c.ordersCount <= 0) return false;
      if (filters.governorates.length && !filters.governorates.includes(governorateKey(c)))
        return false;
      return true;
    });
    return pool.slice().sort(sortComparator(prefs.sort, uiLocale));
  }, [allCustomers, filters, prefs.sort, uiLocale]);

  const groups = useMemo(
    () => buildGroups(filtered, prefs.group, uiLocale, t(keys.customers.mNoGovernorate)),
    [filtered, prefs.group, uiLocale, t],
  );
  const shown = filtered.length;

  const filterCount = activeFilterCount(filters);
  const hasFilters = filterCount > 0;

  return (
    <div className="flex flex-col">
      {/* Sticky top region: title + actions pin while the list scrolls. */}
      <div
        className="sticky top-0 z-20 -mx-4 border-b border-[color:var(--hair)] bg-[color:var(--canvas)] px-4"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)" }}
      >
        {/* Header row: title + bell + More (search lives in the bottom nav) */}
        <div className="flex items-center justify-between gap-2 px-1 pb-1 pt-1">
          <h1 className="text-[28px] font-extrabold tracking-[-0.025em] text-[color:var(--text-primary)]">
            {t(keys.customers.pageTitle)}
          </h1>
          <div className="flex items-center gap-1 rounded-full bg-[color:var(--soft2)] p-[5px]">
            <MobileHeaderNotificationBell triggerClassName="relative flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]" />
            <span className="h-[17px] w-px bg-[color:var(--border-strong)]" />
            <Menu as="div" className="relative flex">
              <MenuButton
                aria-label={t(keys.orders.mMore)}
                className="flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
              >
                <DotsIcon />
              </MenuButton>
              <MenuItems
                anchor="bottom end"
                className="z-50 mt-1 w-[210px] rounded-[14px] border border-[color:var(--card-br)] bg-[color:var(--card)] p-1.5 shadow-[0_20px_54px_rgba(0,0,0,0.28)] focus:outline-none"
              >
                <ActionMenuItem
                  icon={<SlidersIcon />}
                  label={t(keys.orders.mDisplay)}
                  onClick={() => setDisplayOpen(true)}
                />
              </MenuItems>
            </Menu>
          </div>
        </div>

        {/* Count + Filter row */}
        <div className="flex items-center gap-2.5 px-1 pb-2.5 pt-2.5">
          <span className="text-[13px] font-semibold tracking-[-0.01em] text-[color:var(--text-muted)]">
            {hasFilters
              ? t(keys.customers.mCountFiltered, { shown, total })
              : t(keys.customers.totalLabel, { count: total })}
          </span>
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            aria-label={t(keys.customers.mFilterTitle)}
            className="inline-flex items-center gap-[7px] rounded-full py-2 pe-[13px] ps-[14px] text-[13.5px] font-semibold transition-colors"
            style={{
              background: hasFilters ? "var(--soft2)" : "transparent",
              border: `1px solid ${hasFilters ? "var(--accent-500)" : "var(--line)"}`,
              color: hasFilters ? "var(--text-primary)" : "var(--text-secondary)",
            }}
          >
            <FilterIcon />
            {t(keys.customers.mFilter)}
            {hasFilters ? (
              <span
                className="flex h-[17px] min-w-[17px] items-center justify-center rounded-full px-1 text-[10px] font-extrabold"
                style={{ background: "var(--accent-500)", color: "var(--on-accent)" }}
              >
                {filterCount}
              </span>
            ) : null}
          </button>
        </div>
      </div>

      {/* List */}
      {isLoading && !data ? (
        <div className="px-1 py-10 text-center text-[13px] text-[color:var(--text-muted)]">
          {t(keys.common.loading)}
        </div>
      ) : error ? (
        <div className="px-1 py-10 text-center text-[13px] text-[color:var(--text-muted)]">
          {t(keys.customers.loadError)}
        </div>
      ) : shown === 0 ? (
        <EmptyState
          hasFilters={hasFilters}
          onClear={() => changeFilters(EMPTY_CUSTOMERS_FILTERS)}
          t={t}
        />
      ) : (
        <div className="pt-1">
          {groups.map((g, gi) => {
            const isCollapsed = !!collapsed[g.key];
            return (
              <div key={g.key} className={gi === 0 ? "" : "border-t border-[color:var(--hair)]"}>
                {g.showHeader ? (
                  <button
                    type="button"
                    onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
                    className="flex w-full items-center gap-2.5 px-1 py-3 text-start transition-colors hover:bg-[color:var(--soft)]"
                  >
                    <ChevronIcon rotated={isCollapsed} />
                    <PinIcon />
                    <span className="text-[13.5px] font-semibold tracking-[-0.01em] text-[color:var(--text-primary)]">
                      {g.label}
                    </span>
                    <span className="text-[12.5px] font-semibold text-[color:var(--text-muted)]">
                      {g.rows.length}
                    </span>
                  </button>
                ) : null}
                {!isCollapsed
                  ? g.rows.map((c, i) => (
                      <CustomerRow
                        key={c.id}
                        customer={c}
                        locale={locale}
                        uiLocale={uiLocale}
                        propsShown={prefs.props}
                        onOpen={onOpenCustomer}
                        last={i === g.rows.length - 1}
                        t={t}
                      />
                    ))
                  : null}
              </div>
            );
          })}
          {canLoadMore ? (
            <div className="flex justify-center px-1 pb-2 pt-5">
              <button
                type="button"
                onClick={() => setLimit((l) => l + PAGE_SIZE)}
                className="inline-flex items-center gap-2 rounded-[11px] bg-[color:var(--soft2)] px-4 py-2.5 text-[13.5px] font-semibold text-[color:var(--text-primary)] transition-opacity hover:opacity-90"
              >
                {t(keys.orders.loadMore)}
              </button>
            </div>
          ) : null}
          <div className="px-1 pb-2 pt-3 text-center text-[11.5px] font-medium text-[color:var(--text-disabled)]">
            {t(keys.customers.totalLabel, { count: shown })}
          </div>
        </div>
      )}

      <CustomersDisplaySheet
        isOpen={displayOpen}
        onClose={() => setDisplayOpen(false)}
        prefs={prefs}
        onGroup={setGroup}
        onSort={setSort}
        onToggleProp={toggleProp}
        onResetProps={resetProps}
      />
      <CustomersFilterSheet
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        governorates={governorateOptions}
        value={filters}
        onChange={changeFilters}
      />
    </div>
  );
}

function formatJoinShort(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-US", {
    month: "short",
    year: "numeric",
  }).format(date);
}

function CustomerRow({
  customer,
  locale,
  uiLocale,
  propsShown,
  onOpen,
  last,
  t,
}: {
  customer: CustomerItem;
  locale: Locale;
  uiLocale: Locale;
  propsShown: RowProps;
  onOpen: (id: string) => void;
  last: boolean;
  t: ReturnType<typeof useTranslations>;
}) {
  const tint = tintFor(customer.name || "?");
  const gov = governorateName(customer, uiLocale);

  // Meta line — toggleable parts joined by " · ".
  const parts: string[] = [];
  if (propsShown.governorate && gov) parts.push(gov);
  if (propsShown.orders) {
    parts.push(t(keys.customers.ordersCountLabel, { count: customer.ordersCount }));
  }

  return (
    <div className="relative flex items-center gap-1.5 px-1 transition-colors hover:bg-[color:var(--soft)]">
      <button
        type="button"
        onClick={() => onOpen(customer.id)}
        className="flex min-w-0 flex-1 items-center gap-3 py-[11px] text-start"
      >
        {/* Avatar */}
        <span
          aria-hidden="true"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[14px] font-extrabold tracking-[-0.02em]"
          style={{ background: tint.bg, color: tint.fg }}
        >
          {initialsOf(customer.name || "?")}
        </span>

        {/* Name + meta */}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13.5px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
            {customer.name}
          </div>
          {parts.length ? (
            <div className="mt-0.5 truncate text-[12px] text-[color:var(--text-muted)]">
              {parts.map((p, i) => (
                <span key={i}>
                  {i > 0 ? <span className="text-[color:var(--text-disabled)]"> · </span> : null}
                  {p}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* Right: total spent + since */}
        <div className="shrink-0 text-end">
          <div className="text-[14.5px] font-extrabold tracking-[-0.01em] text-[color:var(--text-primary)]">
            <bdi>{formatCurrency(customer.totalSpent, locale)}</bdi>
          </div>
          {propsShown.since ? (
            <div className="mt-0.5 text-[11.5px] text-[color:var(--text-muted)]">
              {formatJoinShort(customer.createdAt, locale)}
            </div>
          ) : null}
        </div>
      </button>

      {!last ? (
        <span className="absolute bottom-0 end-0 start-[59px] h-px bg-[color:var(--hair)]" />
      ) : null}
    </div>
  );
}

function ActionMenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <MenuItem>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2.5 text-start text-[14px] font-medium text-[color:var(--text-primary)] data-[focus]:bg-[color:var(--soft)]"
      >
        <span className="text-[color:var(--text-muted)]">{icon}</span>
        {label}
      </button>
    </MenuItem>
  );
}

function EmptyState({
  hasFilters,
  onClear,
  t,
}: {
  hasFilters: boolean;
  onClear: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-10 py-16 text-center">
      <div className="mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-[15px] bg-[color:var(--soft)] text-[color:var(--text-muted)]">
        <UsersIcon />
      </div>
      <div className="text-[15px] font-semibold tracking-[-0.01em] text-[color:var(--text-secondary)]">
        {t(keys.customers.emptyTitle)}
      </div>
      <div className="mt-1.5 max-w-[230px] text-[13px] leading-relaxed text-[color:var(--text-muted)]">
        {t(keys.customers.emptyBody)}
      </div>
      {hasFilters ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-4 rounded-[11px] bg-[color:var(--soft2)] px-4 py-2.5 text-[13.5px] font-semibold text-[color:var(--text-primary)]"
        >
          {t(keys.common.clearFilters)}
        </button>
      ) : null}
    </div>
  );
}

// ─── icons ───────────────────────────────────────────────────────────────────

const ICON = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function DotsIcon() {
  return (
    <svg {...ICON} stroke="none" fill="currentColor">
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  );
}
function SlidersIcon() {
  return (
    <svg {...ICON}>
      <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h7M15 18h5" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="13" cy="18" r="2" />
    </svg>
  );
}
function FilterIcon() {
  return (
    <svg {...ICON} width={16} height={16} strokeWidth={1.9}>
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  );
}
function ChevronIcon({ rotated }: { rotated: boolean }) {
  return (
    <svg
      {...ICON}
      width={11}
      height={11}
      strokeWidth={3}
      className="shrink-0 text-[color:var(--text-muted)] transition-transform"
      style={{ transform: rotated ? "rotate(-90deg)" : "rotate(0deg)" }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[color:var(--text-muted)]"
    >
      <path d="M12 21s7-5.7 7-11a7 7 0 1 0-14 0c0 5.3 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.4a3 3 0 0 1 0 5.2" />
      <path d="M18 19a5.3 5.3 0 0 0-3-4.8" />
    </svg>
  );
}
